import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, TextInput, View } from 'react-native';
import { useSelector } from 'react-redux';

import { ProfileImage, RedChipsPng } from '../../../assets';
import { getSocket } from '../../../services/socket';
import { RootState } from '../../../store';
import { env } from '../../../utils/env';
import { ACHIEVEMENTS_FONT as FONT } from '../Achievements/achievementsTheme';
import { CtaButton } from '../common/CtaButton';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { Pressable } from '../common/Touchable';
import RedesignModal from './RedesignModal';
import { POPUP, POPUP_TINT, popupBodyStyle, popupColors, PopupHeader } from './RedesignPopup';
import { useRedesignScale } from './useRedesignScale';

/**
 * Invite a player to the table, opened from the game's empty-seat "Invite" discs. Drawn in the
 * popups' language ("Modal / Settings" siblings): the dark maroon panel with the gold title and
 * close glyph, a sunken search well, surface/raised player rows with the orange hairline, and the
 * green CTA per row. Logic (socket invite, search, paging) is unchanged.
 */
const PANEL_HEIGHT = 480;
const ROW = { height: 56, radius: 10, padding: 8, avatar: 40, badge: 18, gap: 10 };
const SEARCH = { height: 38, radius: 8 };

interface InvitePlayerToTableModalProps {
	onClose: () => void;
	tableId?: number | string;
	tableName?: string;
}

const PLAYERS_PER_PAGE = 5;

function InvitePlayerToTableModal({ onClose, tableId }: InvitePlayerToTableModalProps) {
	const { t } = useTranslation();
	const [searchQuery, setSearchQuery] = useState('');
	const auth = useSelector((s: RootState) => s.auth);
	const scale = useRedesignScale({ fitHeight: PANEL_HEIGHT });
	const styles = useMemo(() => createStyles(scale), [scale]);

	// Get socket using the proper service
	const socket =
		auth.user?.id && auth.user?.username
			? getSocket(String(auth.user.id), String(auth.user.username))
			: null;
	const [inviteSent, setInviteSent] = useState(false);
	const [displayCount, setDisplayCount] = useState(PLAYERS_PER_PAGE);
	const activePlayers = useSelector((s: RootState) => s.activePlayers.activePlayers);
	// Ensure activePlayers is an array before filtering
	const safeActivePlayers = Array.isArray(activePlayers) ? activePlayers : [];
	const filteredPlayers = safeActivePlayers.filter(player =>
		player?.username?.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	const displayedPlayers = filteredPlayers.slice(0, displayCount);
	const hasMore = displayCount < filteredPlayers.length;

	// Reset displayCount when search query changes
	useEffect(() => {
		setDisplayCount(PLAYERS_PER_PAGE);
	}, [searchQuery]);

	const getAvatarUrl = (avatarLink: string | null) => {
		if (!avatarLink) return null;
		const baseUrl = env('SVARA_API_URL') || 'http://localhost:9000';
		return `${baseUrl}/api/files/${avatarLink}`;
	};

	const handleInvite = (inviteeId: number) => {
		if (!socket || !tableId) {
			console.error('Socket or tableId not available');
			return;
		}

		const invitePayload = {
			inviter: auth.user?.id,
			invitee: inviteeId,
			tableId: tableId,
		};

		socket.emit('svara:inviteUser', invitePayload);
		setInviteSent(true);
	};

	const handleLoadMore = () => {
		setDisplayCount(prev => Math.min(prev + PLAYERS_PER_PAGE, filteredPlayers.length));
	};

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{ height: PANEL_HEIGHT, radius: POPUP.radius, tint: POPUP_TINT, overScreen: true }}
		>
			<View style={styles.body}>
				<PopupHeader
					scale={scale}
					title={t('T_SITE_INVITE_PLAYERS_TITLE', { defaultValue: 'Players' })}
					onClose={onClose}
					closeLabel={t('T_SITE_A11Y_CLOSE_MODAL', { defaultValue: 'Close modal' })}
				/>

				<TextInput
					style={styles.searchInput}
					placeholder={t('T_SITE_SEARCH_PLAYERS', { defaultValue: 'Search players...' })}
					placeholderTextColor={popupColors.muted}
					value={searchQuery}
					onChangeText={setSearchQuery}
				/>
				{filteredPlayers.length > 0 && (
					<Text style={styles.countText}>
						{t('T_SITE_INVITE_SHOWING_PLAYERS', {
							defaultValue: 'Showing {{shown}} of {{total}} players',
							shown: displayedPlayers.length,
							total: filteredPlayers.length,
						})}
					</Text>
				)}

				<IndicatorScrollView
					style={styles.scrollView}
					contentContainerStyle={styles.scrollContent}
					showsVerticalScrollIndicator={true}
					nestedScrollEnabled={true}
				>
					{filteredPlayers.length === 0 ? (
						<View style={styles.emptyState}>
							<Text style={styles.emptyText}>
								{searchQuery
									? t('T_SITE_INVITE_NO_PLAYERS_FOUND', { defaultValue: 'No players found' })
									: t('T_SITE_INVITE_NO_ACTIVE_PLAYERS', {
											defaultValue: 'No active players found',
										})}
							</Text>
						</View>
					) : (
						<>
							{displayedPlayers.map(player => {
								const avatarUrl = getAvatarUrl(player.avatarLink);
								return (
									<View key={player.id} style={styles.playerRow}>
										<View style={[styles.avatarWell, styles.rowCenter]}>
											<Image
												source={avatarUrl ? { uri: avatarUrl } : ProfileImage}
												style={styles.avatar}
												resizeMode='cover'
											/>
											<View style={styles.levelBadge}>
												<Text style={styles.levelText} numberOfLines={1}>
													{player.level}
												</Text>
											</View>
										</View>
										<View style={[styles.nameColumn, styles.rowCenter]}>
											<Text style={styles.nameText} numberOfLines={1}>
												{player.username}
											</Text>
											<View style={styles.chipsRow}>
												<Image source={RedChipsPng} style={styles.chipsIcon} resizeMode='contain' />
												<Text style={styles.chipsText} numberOfLines={1}>
													{player.chips.toLocaleString()}
												</Text>
											</View>
										</View>
										<CtaButton
											label={
												inviteSent
													? t('T_SITE_INVITE_SENT', { defaultValue: 'Sent' })
													: t('T_Invite', { defaultValue: 'Invite' })
											}
											disabled={inviteSent}
											onPress={() => handleInvite(player.id)}
											style={styles.inviteButton}
											surfaceStyle={styles.inviteSurface}
											labelStyle={styles.inviteLabel}
										/>
									</View>
								);
							})}

							{/* Load More Button */}
							{hasMore && (
								<Pressable
									accessibilityRole='button'
									onPress={handleLoadMore}
									style={({ pressed }) => [styles.loadMoreButton, pressed && styles.pressed]}
								>
									<Text style={styles.loadMoreText}>
										{t('T_SITE_INVITE_LOAD_MORE', {
											defaultValue: 'Load More ({{remaining}} remaining)',
											remaining: filteredPlayers.length - displayCount,
										})}
									</Text>
								</Pressable>
							)}
						</>
					)}
				</IndicatorScrollView>
			</View>
		</RedesignModal>
	);
}

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const hairline = Math.max(1, scale);
	return StyleSheet.create({
		body: { ...popupBodyStyle(scale), gap: s(8) },
		searchInput: {
			height: s(SEARCH.height),
			borderRadius: s(SEARCH.radius),
			paddingHorizontal: s(12),
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(14),
			color: popupColors.primary,
			borderWidth: hairline,
			borderColor: popupColors.borderGold,
			backgroundColor: popupColors.sunken,
		},
		countText: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(12),
			lineHeight: s(16),
			color: popupColors.secondary,
			textAlign: 'center',
		},
		scrollView: {
			flex: 1,
		},
		scrollContent: {
			gap: s(8),
			paddingBottom: s(4),
		},
		playerRow: {
			flexDirection: 'row',
			alignItems: 'stretch',
			gap: s(ROW.gap),
			height: s(ROW.height),
			paddingLeft: s(ROW.padding),
			borderRadius: s(ROW.radius),
			borderWidth: hairline,
			borderColor: popupColors.plateBorder,
			backgroundColor: popupColors.raised,
			overflow: 'hidden',
		},
		rowCenter: {
			alignSelf: 'center',
		},
		avatarWell: {
			width: s(ROW.avatar),
			height: s(ROW.avatar),
		},
		avatar: {
			width: s(ROW.avatar),
			height: s(ROW.avatar),
			borderRadius: s(ROW.avatar / 2),
			borderWidth: hairline,
			borderColor: popupColors.borderGold,
			backgroundColor: popupColors.sunken,
		},
		levelBadge: {
			position: 'absolute',
			right: s(-4),
			bottom: s(-2),
			minWidth: s(ROW.badge),
			height: s(ROW.badge),
			paddingHorizontal: s(3),
			borderRadius: s(ROW.badge / 2),
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: popupColors.gold200,
			borderWidth: hairline,
			borderColor: popupColors.goldRim,
		},
		levelText: {
			fontFamily: FONT,
			fontWeight: '800',
			fontSize: s(10),
			color: popupColors.onGold,
		},
		nameColumn: {
			flex: 1,
			gap: s(2),
		},
		nameText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(15),
			color: popupColors.gold50,
		},
		chipsRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(5),
		},
		chipsIcon: {
			width: s(16),
			height: s(16),
		},
		chipsText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(13),
			color: '#FFFF99',
		},
		// the CTA closes the row: full height, flush to the row's right edge
		inviteButton: {
			minWidth: s(92),
			alignSelf: 'stretch',
			justifyContent: 'center',
			borderRadius: 0,
			borderTopRightRadius: s(ROW.radius) - hairline,
			borderBottomRightRadius: s(ROW.radius) - hairline,
		},
		inviteSurface: {
			flex: 1,
			borderRadius: 0,
			borderTopRightRadius: s(ROW.radius) - hairline,
			borderBottomRightRadius: s(ROW.radius) - hairline,
		},
		inviteLabel: {
			fontSize: s(13),
		},
		emptyState: {
			padding: s(32),
			alignItems: 'center',
			justifyContent: 'center',
		},
		emptyText: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(15),
			color: popupColors.secondary,
		},
		loadMoreButton: {
			height: s(40),
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: s(ROW.radius),
			borderWidth: hairline,
			borderColor: popupColors.plateBorder,
			backgroundColor: popupColors.raised,
		},
		pressed: {
			opacity: 0.85,
		},
		loadMoreText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(13),
			color: popupColors.gold200,
		},
	});
};

export default InvitePlayerToTableModal;
